import SearchInput from '@/components/common/SearchInput/Search'
import ControllersTableView, {
  DECODER_TYPE_BY_TAB,
} from '@/features/config/pages/Controllers/ControllersTableView'
import { RefreshIcon } from '@/components/icons'
import ArrowUploadSetup from '@/components/icons/ArrowUploadSetup'
import { CONFIG_SETUP_ROUTES } from '@/constant/route'
import { ConfigUIProvider } from '@/features/config/context'
import useSearchAllDecoders from '@/features/config/hooks/controller/useSearchAllDecoders'
import useMk2ImportSettling from '@/features/config/hooks/controller/useMk2ImportSettling'
import { useSetUpUI } from '@/features/design-system/context'
import { TabContext } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { memo, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SetUpLayout from '../../../../../common/SetUpLayout'
import DiscoverHeader from '../DiscoverHeader'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'

type Props = {
  open: boolean
  isResyncing: boolean
  onResync: () => void
}

const ReviewControllers = memo(({ open, isResyncing, onResync }: Props) => {
  const { t } = useTranslation()

  const { openModal, setModalView } = useSetUpUI()

  const navigate = useNavigate()
  // SCRUM-976: pin to URL :id, not the global selected interface (AuthContext/localStorage).
  const { id: routeId } = useParams()
  const interfaceId = Number(routeId)

  const [tab, setTab] = useState<string>('0')
  const [keyword, setKeyword] = useState('')

  // The same whole-list query the grid below runs, so the total and the rows
  // come from one fetch rather than two.
  const { data: allDecoders, isFetching } = useSearchAllDecoders(
    Number(interfaceId),
    DECODER_TYPE_BY_TAB[tab],
    { enabled: Number.isFinite(Number(interfaceId)) },
  )

  const totalCount = allDecoders?.length ?? 0

  // SCRUM-1032 QC round 7, item 11 — an MK2 import is still waiting for its
  // decoders. Until they land the count below is the old one (often 0), so it
  // says so instead; and a Resync now would race the import it is waiting on.
  const isImportSettling = useMk2ImportSettling(interfaceId)

  const onBack = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(interfaceId + ''))
  }

  // A tab is a different list; the search from the last one means nothing here.
  useEffect(() => {
    setKeyword('')
  }, [tab])

  const handleChangeTab = (_: any, newTab: string) => {
    setTab(newTab)
  }

  const handleDone = () => {
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(interfaceId + ''))
  }

  const handleOpenMk2Decoder = () => {
    openModal()
    setModalView({ type: 'MK2_DECODER_MODAL_V2' })
  }

  return (
    <Dialog fullScreen open={open} onClose={onBack}>
      <SetUpLayout
        title={t('reviewDecoders')}
        header={
          <DiscoverHeader
            title={t('reviewDecoders')}
            onBack={onBack}
            actions={
              <>
                <Button
                  variant="secondary"
                  startIcon={
                    isResyncing ? (
                      <CircularProgress
                        size={14}
                        sx={{ color: 'primary.main' }}
                      />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  disabled={isResyncing || isImportSettling}
                  onClick={onResync}
                  data-testid="toolbar-resync"
                  className={classes.toolbarButton}
                >
                  {isResyncing
                    ? t('controllersSyncing')
                    : t('discoveryDataResync')}
                </Button>

                {tab === '0' && (
                  <Button
                    variant="secondary"
                    startIcon={<ArrowUploadSetup />}
                    onClick={handleOpenMk2Decoder}
                    // A second import or a resync racing the first: either
                    // can start another automatic resync on top of this one.
                    disabled={isResyncing || isImportSettling}
                    data-testid="toolbar-import-mk2"
                    className={classes.toolbarButton}
                  >
                    {t('discoveryDataImportMk2')}
                  </Button>
                )}

                <Button
                  variant="primary"
                  onClick={handleDone}
                  className={classes.toolbarButton}
                >
                  {t('commonActionsDone')}
                </Button>
              </>
            }
          />
        }
        footer={undefined}
        // The layout's own 25px/32px is generous for a screen that is one list:
        // the padding is set here instead, tighter at the top where it was
        // costing the list a row and a half.
        fullWidth
        // The grid measures its scroller once, on mount, and caps itself
        // against the viewport only afterwards. In an unconstrained layout it
        // measured a scroller as tall as all 987 rows, put every one of them
        // in the DOM and never re-measured — which is the pause on coming back
        // to the Output tab, and the height the page gained with it. This
        // bounds the page to the viewport, as the guided setup does, so the
        // grid is inside a settled box from the first paint and virtualizes.
        constrainViewport
      >
        <Stack sx={{ p: '14px 24px 16px', flex: 1, minHeight: 0 }}>
          {/* One line for "which decoders" and "which of them": the tabs choose
            the list, the search narrows it. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ gap: '16px', mb: '12px', flexWrap: 'wrap' }}
          >
            <TabContext value={tab}>
              {/* A segmented control, not three tabs with a drop shadow: the
                container is the track, the selected tab sits in it. */}
              <Tabs
                value={tab}
                onChange={handleChangeTab}
                textColor="inherit"
                sx={{
                  minHeight: 34,
                  height: 34,
                  p: '3px',
                  borderRadius: '9px',
                  border: '1px solid',
                  borderColor: 'border.main',
                  backgroundColor: 'background.overrideDefault',
                  // The track is border-box: its 34px already contains the 1px
                  // border and 3px of padding either side, leaving 26px for the
                  // tab inside it. A 28px tab was two pixels taller than the
                  // room it had, and the scroller clipped the bottom of the
                  // selected pill.
                  '.MuiTabs-scroller': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '.MuiTabs-flexContainer': {
                    height: '100%',
                    alignItems: 'center',
                    gap: '2px',
                  },
                  '.MuiTab-root': {
                    minHeight: 26,
                    height: 26,
                    minWidth: 92,
                    px: '16px',
                    borderRadius: '7px',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: 1,
                    textTransform: 'none',
                    color: 'grey.800',
                    '&.Mui-selected': {
                      color: '#ffffff',
                      backgroundColor: 'primary.main',
                    },
                  },
                  '.MuiTabs-indicator': { display: 'none' },
                }}
              >
                <Tab value={'0'} label={t('discoveryDataTabOutput')} />
                <Tab value={'1'} label={t('discoveryDataTabInput')} />
                {/* SDI-12 decoders are discovered like the rest and were the one
                  kind this screen never showed. */}
                <Tab value={'2'} label={t('discoveryDataTabSdi12')} />
              </Tabs>
            </TabContext>

            <SearchInput
              name={t('discoveryDataSearchPlaceholder')}
              value={keyword}
              onChange={(event: any) => setKeyword(event.target.value)}
              sx={{ width: '300px', height: '34px', boxShadow: 'none' }}
            />

            <Box sx={{ flexGrow: 1 }} />

          <Typography className={classes.subtitle} data-testid="decoder-count">
            {isImportSettling
              ? t('discoveryDataWaitingForImport')
              : isFetching
              ? t('discoveryDataLoadingDecoders')
              : // The count is this tab's, not the controller's: "1 decoder
                // found on this controller" on both Output and Input read as a
                // controller with one decoder (SCRUM-1032 QC, 30 Oct).
                t(
                  totalCount === 1
                    ? 'discoveryDataDecoderFoundOnTabOne'
                    : 'discoveryDataDecodersFoundOnTabOther',
                  { count: totalCount },
                )}
          </Typography>
        </Stack>

        {/* SCRUM-1032 QC round 4, item 2 — the spinner is for the first load,
            not for every refetch.
            `isFetching` here used to take the grid off the screen whenever the
            decoder list was re-read, and anything that touches a decoder does
            re-read it: On / Off / Ping / Autoswitch invalidate the controller
            key, the grid's cache bridge turns that into a refetch, and this
            guard then unmounted the grid mid-scroll. What came back was a new
            grid at row 0 — the "auto revert to the top" Wei Woon filmed on
            27 Sep, with one frame of empty list in the middle of it.
            The grid holds the whole list itself here and takes `isFetching` as
            its own `loading` prop, so it already draws an overlay over the rows
            it is showing while the new ones arrive. A background refetch needs
            nothing from this guard. */}
        {!allDecoders && (
          <Stack alignItems="center" mt="90px">
            <CircularProgress />
          </Stack>
        )}
        {allDecoders && (
          <Stack flexGrow="1" sx={{ minHeight: 0, gap: '14px' }}>
            {/* The decoder list itself, not a copy of it. Same columns, same
                filters, same control panel as the guided setup's decoder step
                — in read-only mode, since discovery is for looking at what the
                controller returned, not for editing it. */}
              {/* A height, not just a flex grow. The grid caps itself against
                the viewport only after it has measured, and until then its
                body is as tall as its rows — which on a thousand decoders
                meant the virtualizer measured an unbounded scroller on mount
                and rendered every row into the DOM. Bounded by CSS from the
                first paint, it renders the dozen rows on screen. */}
            <Box
              sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex' }}
            >
              {/* Keyed by tab: the table holds its edits and scroll in its
                  own state, so switching decoder type has to remount it. */}
              <ControllersTableView
                key={tab}
                tab={tab}
                interfaceId={Number(interfaceId)}
                keyword={keyword}
                bulkEditMode={false}
                embedded
                review
              />
            </Box>
          </Stack>
        )}
        {/* SCRUM-1032 QC item 4 — no second empty state here.
            An empty array is truthy, so on a tab the controller returned
            nothing for (SDI-12, usually) the block above still rendered the
            grid, which draws its own "no data" illustration — and this sibling
            drew another one under it. That is the "two SDI-12 icons with no
            data behind them". The grid's own empty state is the better of the
            two anyway: it keeps the column headers and the search in place. */}
        </Stack>
      </SetUpLayout>
    </Dialog>
  )
})

// The decoder control panel this screen embeds is the `/config` one, and parts
// of it (the delete-decoder dialog) read `useConfigUI()`. Without the provider
// that read is undefined and the panel throws as it opens: the "internal server
// error" on clicking a row's control button. `SetUpGuided` hosts the same
// provider for the same reason.
const ReviewControllersWithConfigUI = memo((props: Props) => (
  <ConfigUIProvider>
    <ReviewControllers {...props} />
  </ConfigUIProvider>
))

export default ReviewControllersWithConfigUI
